// Parse a typed slot selection for the sorting label printer.
//
// Accepted, comma-separated:
//   A1              one slot
//   A1-D6           a BLOCK — shelves A..D crossed with positions 1..6
//   (blank)         every slot on the bay
//
// A range is rectangular, not a linear run through the slot order. That's the
// reading that matches the hardware: on the Newport put wall, positions 1-6 are
// the left-hand bay, so "A1-D6" prints exactly that bay's labels. A linear
// reading would instead give A1..A18, B1..B18, C1..C18, D1..D6, which is not
// anything you can walk up to.
//
// Endpoints may be given in either order — "D6-A1" is the same block as
// "A1-D6".

export interface SelectableSlot { shelf: string; position: number | string }

export type SlotSelectionResult<T> =
    | { ok: true; slots: T[] }
    | { ok: false; badToken: string };

const SINGLE = /^([A-E])(\d{1,2})$/;
const RANGE = /^([A-E])(\d{1,2})\s*-\s*([A-E])(\d{1,2})$/;

/** Slots matching `spec`, in the order they appear in `all`. Blank spec = all.
 *  Unknown slots simply don't match — a valid-looking "E1" on a bay with no E
 *  shelf yields an empty list rather than an error, so the caller can say
 *  "0 labels" instead of blaming the typing. */
export function selectSlots<T extends SelectableSlot>(spec: string, all: T[]): SlotSelectionResult<T> {
    const trimmed = spec.trim();
    if (!trimmed) return { ok: true, slots: all };

    const tests: Array<(shelf: string, position: number) => boolean> = [];
    for (const raw of trimmed.split(",")) {
        const token = raw.trim().toUpperCase();
        if (!token) continue;                      // tolerate "A1,, B2" and a trailing comma

        const range = RANGE.exec(token);
        if (range) {
            const [, s1, p1, s2, p2] = range;
            const shelfLo = s1 <= s2 ? s1 : s2;
            const shelfHi = s1 <= s2 ? s2 : s1;
            const posLo = Math.min(Number(p1), Number(p2));
            const posHi = Math.max(Number(p1), Number(p2));
            tests.push((shelf, position) =>
                shelf >= shelfLo && shelf <= shelfHi && position >= posLo && position <= posHi);
            continue;
        }

        const single = SINGLE.exec(token);
        if (single) {
            const [, s, p] = single;
            const pos = Number(p);
            tests.push((shelf, position) => shelf === s && position === pos);
            continue;
        }

        return { ok: false, badToken: raw.trim() };
    }

    if (!tests.length) return { ok: true, slots: all };
    const slots = all.filter(s => {
        const shelf = String(s.shelf).toUpperCase();
        const position = Number(s.position);
        return tests.some(t => t(shelf, position));
    });
    return { ok: true, slots };
}
